import React, { useState } from 'react';
import { 
  Box, 
  Typography, 
  Paper, 
  Table, 
  TableBody, 
  TableCell, 
  TableContainer, 
  TableHead, 
  TableRow,
  Grid,
  Card,
  CardContent,
  Chip,
  LinearProgress,
  TableSortLabel
} from '@mui/material';
import TrendingUpIcon from '@mui/icons-material/TrendingUp';
import TrendingDownIcon from '@mui/icons-material/TrendingDown';
import MonetizationOnIcon from '@mui/icons-material/MonetizationOn';
import BarChartIcon from '@mui/icons-material/BarChart';
import CategoryIcon from '@mui/icons-material/Category';
import categoryOptions from '../../../utils/categoryOptions';

const KeywordStats = ({ keyword, stats }) => {
  // State for sorting
  const [orderBy, setOrderBy] = useState('date');
  const [order, setOrder] = useState('desc');

  // Calculate totals
  const totalHits = stats.dailyStats.reduce((sum, day) => sum + day.hits, 0);
  const totalClicks = stats.dailyStats.reduce((sum, day) => sum + (day.clicks || 0), 0);
  const totalRevenue = stats.dailyStats.reduce((sum, day) => sum + day.revenue, 0);
  const avgPayout = totalClicks > 0 ? totalRevenue / totalClicks : 0;
  
  // Calculate coverage from stats instead of using keyword.coverage
  const totalMonetized = stats.dailyStats.reduce((sum, day) => sum + day.monetized, 0);
  const calculatedCoverage = totalHits > 0 ? (totalMonetized / totalHits) * 100 : 0;

  // Format date function
  const formatDate = (dateString) => {
    const options = { year: 'numeric', month: 'short', day: 'numeric' };
    return new Date(dateString).toLocaleDateString(undefined, options);
  };

  // Get color for coverage progress bar
  const getCoverageColor = (coverage) => {
    if (coverage >= 70) return 'success';
    if (coverage >= 40) return 'warning';
    return 'error';
  };

  const getCategoryColor = (category) => {
    const categoryLower = category?.toLowerCase();
    
    // Find the matching category from our shared options
    const categoryOption = categoryOptions.find(option => 
      option.value === categoryLower && !option.header
    );
    
    // If no match is found, return the default color
    if (!categoryOption) {
      return '#757575'; // Grey
    }
    
    // Return colors based on category
    switch(categoryLower) {
      case 'finance': return '#1976d2'; // Blue
      case 'health': return '#2e7d32'; // Green
      case 'technology': return '#9c27b0'; // Purple
      case 'education': return '#ed6c02'; // Orange
      case 'entertainment': return '#d81b60'; // Pink
      case 'travel': return '#00acc1'; // Cyan
      case 'shopping': return '#5e35b1'; // Deep Purple
      case 'automotive': return '#546e7a'; // Blue Grey
      case 'real_estate': return '#6d4c41'; // Brown
      case 'food': return '#f57c00'; // Deep Orange
      case 'sports': return '#43a047'; // Light Green
      case 'news': return '#c62828'; // Red
      case 'business': return '#283593'; // Indigo
      case 'lifestyle': return '#ad1457'; // Pink
      case 'general': return '#757575'; // Grey
      default: return '#757575'; // Grey
    }
  };

  // Handle sort request
  const handleRequestSort = (property) => {
    const isAsc = orderBy === property && order === 'asc';
    setOrder(isAsc ? 'desc' : 'asc');
    setOrderBy(property);
  };

  // Create sort handler
  const createSortHandler = (property) => () => {
    handleRequestSort(property);
  };

  // Sort function
  const sortData = (data, comparator) => {
    const stabilizedThis = data.map((el, index) => [el, index]);
    stabilizedThis.sort((a, b) => {
      const order = comparator(a[0], b[0]);
      if (order !== 0) return order;
      return a[1] - b[1];
    });
    return stabilizedThis.map((el) => el[0]);
  };

  // Get comparator
  const getComparator = (order, orderBy) => {
    return order === 'desc'
      ? (a, b) => descendingComparator(a, b, orderBy)
      : (a, b) => -descendingComparator(a, b, orderBy);
  };

  // Descending comparator
  const descendingComparator = (a, b, orderBy) => {
    // Special case for date
    if (orderBy === 'date') {
      return new Date(b.date) - new Date(a.date);
    }
    
    // Special case for coverage
    if (orderBy === 'coverage') {
      const aCoverage = a.hits > 0 ? (a.monetized / a.hits) * 100 : 0;
      const bCoverage = b.hits > 0 ? (b.monetized / b.hits) * 100 : 0;
      return bCoverage - aCoverage;
    }
    
    // Special case for payout
    if (orderBy === 'payout') {
      const aPayout = a.clicks > 0 ? a.revenue / a.clicks : 0;
      const bPayout = b.clicks > 0 ? b.revenue / b.clicks : 0;
      return bPayout - aPayout;
    }
    
    if (b[orderBy] < a[orderBy]) {
      return -1;
    }
    if (b[orderBy] > a[orderBy]) {
      return 1;
    }
    return 0;
  };

  // Sort the daily stats
  const sortedDailyStats = sortData(stats.dailyStats, getComparator(order, orderBy));

  // Get color for payout
  const getPayoutColor = (payout) => {
    if (payout >= 0.30) return 'success.main';    // High payout - Green
    if (payout >= 0.20) return 'primary.main';    // Good payout - Blue
    if (payout >= 0.15) return 'warning.main';    // Decent payout - Orange
    if (payout >= 0.10) return 'orange';          // Average payout - Light Orange
    if (payout > 0) return 'text.secondary';      // Low but positive - Grey
    return 'error.main';                          // Zero or negative - Red
  };

  // Get icon for payout trend
  const getPayoutIcon = (payout) => {
    if (payout >= 0.15) {
      return <TrendingUpIcon sx={{ color: getPayoutColor(payout), fontSize: 32 }} />;
    } else {
      return <TrendingDownIcon sx={{ color: getPayoutColor(payout), fontSize: 32 }} />;
    }
  };

  return (
    <Box>
      <Box sx={{ display: 'flex', alignItems: 'center', mb: 3 }}>
        <Typography variant="h6" sx={{ fontWeight: 600 }}>
          Keyword Performance
        </Typography>
        {keyword.category && (
          <Chip 
            icon={<CategoryIcon fontSize="small" />}
            label={keyword.category} 
            size="small" 
            sx={{ 
              backgroundColor: getCategoryColor(keyword.category),
              color: 'white',
              textTransform: 'capitalize',
              ml: 2
            }}
          />
        )}
      </Box>

      <Grid container spacing={3} sx={{ mb: 4 }}>
        <Grid item xs={12} sm={6} md={3}>
          <Card elevation={2} sx={{ height: '100%', borderRadius: '12px' }}>
            <CardContent sx={{ p: 2 }}>
              <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
                <Box>
                  <Typography variant="body2" color="text.secondary">Total Hits</Typography>
                  <Typography variant="h5" sx={{ fontWeight: 'bold', my: 1 }}>
                    {totalHits.toLocaleString()}
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    Last 5 days
                  </Typography>
                </Box>
                <BarChartIcon sx={{ color: 'primary.main', fontSize: 32 }} />
              </Box>
            </CardContent>
          </Card>
        </Grid>
        
        <Grid item xs={12} sm={6} md={3}>
          <Card elevation={2} sx={{ height: '100%', borderRadius: '12px' }}>
            <CardContent sx={{ p: 2 }}>
              <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
                <Box>
                  <Typography variant="body2" color="text.secondary">Total Revenue</Typography>
                  <Typography variant="h5" sx={{ fontWeight: 'bold', my: 1 }}>
                    ${totalRevenue.toFixed(2)}
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    Last 5 days
                  </Typography>
                </Box>
                <MonetizationOnIcon sx={{ color: 'success.main', fontSize: 32 }} />
              </Box>
            </CardContent>
          </Card>
        </Grid>
        
        <Grid item xs={12} sm={6} md={3}>
          <Card elevation={2} sx={{ height: '100%', borderRadius: '12px' }}>
            <CardContent sx={{ p: 2 }}>
              <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
                <Box>
                  <Typography variant="body2" color="text.secondary">Avg Payout ($/click)</Typography>
                  <Typography variant="h5" sx={{ fontWeight: 'bold', my: 1 }}>
                    ${avgPayout.toFixed(3)}
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    Last 5 days
                  </Typography>
                </Box>
                {getPayoutIcon(avgPayout)}
              </Box>
            </CardContent>
          </Card>
        </Grid>

        <Grid item xs={12} sm={6} md={3}>
          <Card elevation={2} sx={{ height: '100%', borderRadius: '12px' }}>
            <CardContent sx={{ p: 2 }}>
              <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
                <Box>
                  <Typography variant="body2" color="text.secondary">Coverage</Typography>
                  <Typography variant="h5" sx={{ fontWeight: 'bold', my: 1 }}>
                    {calculatedCoverage.toFixed(2)}%
                  </Typography>
                  <Box sx={{ width: '100%', mt: 1, mb: 1 }}>
                    <LinearProgress 
                      variant="determinate" 
                      value={Number(calculatedCoverage)} 
                      color={getCoverageColor(Number(calculatedCoverage))}
                      sx={{ height: 8, borderRadius: 4 }}
                    />
                  </Box>
                  <Typography variant="body2" color="text.secondary">
                    Monetization rate
                  </Typography>
                </Box>
                <BarChartIcon sx={{ color: 'primary.main', fontSize: 32 }} />
              </Box>
            </CardContent>
          </Card>
        </Grid>
      </Grid>

      <Typography variant="h6" sx={{ mb: 2, fontWeight: 600 }}>Daily Performance</Typography>
      <TableContainer component={Paper} variant="outlined" sx={{ borderRadius: '12px' }}>
        <Table size="small">
          <TableHead sx={{ backgroundColor: 'rgba(0, 0, 0, 0.04)' }}>
            <TableRow>
              <TableCell sx={{ fontWeight: 'bold' }}>
                <TableSortLabel
                  active={orderBy === 'date'}
                  direction={orderBy === 'date' ? order : 'asc'}
                  onClick={createSortHandler('date')}
                >
                  Date
                </TableSortLabel>
              </TableCell>
              <TableCell align="right" sx={{ fontWeight: 'bold' }}>
                <TableSortLabel
                  active={orderBy === 'hits'}
                  direction={orderBy === 'hits' ? order : 'asc'}
                  onClick={createSortHandler('hits')}
                >
                  Hits
                </TableSortLabel>
              </TableCell>
              <TableCell align="right" sx={{ fontWeight: 'bold' }}>
                <TableSortLabel
                  active={orderBy === 'monetized'}
                  direction={orderBy === 'monetized' ? order : 'asc'}
                  onClick={createSortHandler('monetized')}
                >
                  Monetized
                </TableSortLabel>
              </TableCell>
              <TableCell align="right" sx={{ fontWeight: 'bold' }}>
                <TableSortLabel
                  active={orderBy === 'clicks'}
                  direction={orderBy === 'clicks' ? order : 'asc'}
                  onClick={createSortHandler('clicks')}
                >
                  Clicks
                </TableSortLabel>
              </TableCell>
              <TableCell align="right" sx={{ fontWeight: 'bold' }}>
                <TableSortLabel
                  active={orderBy === 'coverage'}
                  direction={orderBy === 'coverage' ? order : 'asc'}
                  onClick={createSortHandler('coverage')}
                >
                  Coverage
                </TableSortLabel>
              </TableCell>
              <TableCell align="right" sx={{ fontWeight: 'bold' }}>
                <TableSortLabel
                  active={orderBy === 'revenue'}
                  direction={orderBy === 'revenue' ? order : 'asc'}
                  onClick={createSortHandler('revenue')}
                >
                  Revenue ($)
                </TableSortLabel>
              </TableCell>
              <TableCell align="right" sx={{ fontWeight: 'bold' }}>
                <TableSortLabel
                  active={orderBy === 'payout'}
                  direction={orderBy === 'payout' ? order : 'asc'}
                  onClick={createSortHandler('payout')}
                >
                  Payout ($/click)
                </TableSortLabel>
              </TableCell>
              <TableCell align="right" sx={{ fontWeight: 'bold' }}>Trend</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {sortedDailyStats.map((day) => {
              const payout = day.clicks > 0 ? day.revenue / day.clicks : 0;
              // Calculate daily coverage
              const dayCoverage = day.hits > 0 ? (day.monetized / day.hits) * 100 : 0;
              const coverageColor = getCoverageColor(dayCoverage);
              
              return (
                <TableRow key={day.date}>
                  <TableCell>{formatDate(day.date)}</TableCell>
                  <TableCell align="right">{day.hits}</TableCell>
                  <TableCell align="right">{day.monetized}</TableCell>
                  <TableCell align="right">{day.clicks || 0}</TableCell>
                  <TableCell align="right">
                    <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'flex-end' }}>
                      <Typography variant="body2" sx={{ mr: 1 }}>
                        {dayCoverage.toFixed(2)}%
                      </Typography>
                      <LinearProgress 
                        variant="determinate" 
                        value={Number(dayCoverage)} 
                        color={coverageColor}
                        sx={{ width: 50, height: 6, borderRadius: 3 }}
                      />
                    </Box>
                  </TableCell>
                  <TableCell align="right">${day.revenue.toFixed(2)}</TableCell>
                  <TableCell 
                    align="right"
                    sx={{ 
                      color: getPayoutColor(payout),
                      fontWeight: 'medium'
                    }}
                  >
                    ${payout.toFixed(3)}
                  </TableCell>
                  <TableCell align="right">
                    {getPayoutIcon(payout)}
                  </TableCell>
                </TableRow>
              );
            })}
          </TableBody>
        </Table>
      </TableContainer>
    </Box>
  );
};

export default KeywordStats; 